import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

export type BreadcrumbPathItem = {
  label?: string;
  path?: string;
  loading?: boolean;
  /**
   * Query params for the crumb link. A crumb that leads back to a filtered or
   * scoped view needs them, because routerLink URL-encodes a `?` inside `path`.
   */
  queryParams?: Params;
};

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private breadcrumbs = new BehaviorSubject<BreadcrumbPathItem[]>([]);
  private homeCrumb = new BehaviorSubject<BreadcrumbPathItem | undefined>(
    undefined
  );

  /**
   * Every trail starts at Home, so a page never has to declare it and the way
   * back is always one click. The shell supplies it because the home route
   * depends on the user type.
   */
  breadcrumbs$ = combineLatest([this.breadcrumbs, this.homeCrumb]).pipe(
    map(([breadcrumbs, home]) => {
      // `undefined` means no home crumb; an explicitly empty path is a valid
      // root that simply does not link anywhere.
      if (home?.path === undefined) {
        return breadcrumbs;
      }
      // A page that already starts at home must not show it twice — but an
      // unlinked leading crumb is not home. Pages routinely declare
      // `{ label: 'Management', path: '' }`, and matching those on an empty
      // path would swallow the Home prefix on every one of them.
      const startsAtHome = !!home.path && breadcrumbs[0]?.path === home.path;
      return startsAtHome ? breadcrumbs : [home, ...breadcrumbs];
    })
  );

  public setBreadcrumbs(breadcrumbs: BreadcrumbPathItem[]): void {
    this.breadcrumbs.next(breadcrumbs);
  }

  public setHomeCrumb(home: BreadcrumbPathItem | undefined): void {
    this.homeCrumb.next(home);
  }

  public resetBreadcrumbs(): void {
    this.breadcrumbs.next([]);
  }
}
